import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
// Services
import { toDateTimeISOString } from "../../../Services/string.service";
import { vehicleGroupService } from "../../../Services/vehicle.group.service";
import { vehicleService } from "../../../Services/vehicle.service";
import { storageKeys } from "../../../Constants/storage.key";
import { storage } from "../../../Services/storage.service";

// Icons
// eslint-disable-next-line
import { ButtonGroup, Dropdown } from "react-bootstrap";
import {
  FaCar,
  FaCloudDownloadAlt,
  FaEye,
  FaFileExcel,
  FaTachometerAlt,
  FaRegClock,
  FaAlignLeft,
} from "react-icons/fa";
import { HiOutlineRectangleGroup, HiUser } from "react-icons/hi2";
import { DateTimeFilter } from "../../../components/DateTimeFilter";
import { Filters } from "../../../components/Filters";
import { useSelected } from "../../../contexts/SelectedContext";

export default function VehicleFiltersComponent(props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  // Data
  const [vehicleGroups, setVehicleGroups] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [filtersLoading, setFiltersLoading] = useState(false);

  const [isManualSelection, setIsManualSelection] = useState(false);

  const previousDays = 1;

  // Filters
  const { selectedValues, setSelectedValues } = useSelected();
  const selectedGroup = selectedValues.veiculos?.[0] || "0";

  const pagesWithTodosForVehicleFilter = [
    "gridSpeedControl",
    "gridVisitAmount",
    "gridPeriodVisits",
    "gridProtocols",
    "Radar",
    "Veículo mais próximo",
    "ControlPoint",
    "EletronicFences",
    "RiskArea",
    "globalHeader",
    "VehiclesConfiguration",
  ];

  const vehiclesFilterHasTodos =
    props.page !== "gridJourneyWork" &&
    pagesWithTodosForVehicleFilter.includes(props.page);

  const selectedVehicleRaw = selectedValues.veiculosGroups?.[0] ?? null;

  const defaultVehicleId = resolveDefaultVehicleId(vehicles);

  const selectedVehicle = (() => {
    if (props.page === "gridJourneyWork") {
      if (
        selectedVehicleRaw === undefined ||
        selectedVehicleRaw === null ||
        selectedVehicleRaw === "0" ||
        selectedVehicleRaw === 0 ||
        selectedVehicleRaw === "TODOS"
      ) {
        return defaultVehicleId ?? "0";
      }
      return selectedVehicleRaw;
    }

    if (
      selectedVehicleRaw === undefined ||
      selectedVehicleRaw === null ||
      selectedVehicleRaw === "0" ||
      selectedVehicleRaw === 0
    ) {
      return vehiclesFilterHasTodos ? 0 : defaultVehicleId ?? "0";
    }

    if (selectedVehicleRaw === "TODOS") {
      return 0;
    }

    return selectedVehicleRaw;
  })();
  
  const normalizeVehicleValue = (value) => {
    if (value === null || value === undefined) return "0";
    const numericValue = Number(value);
    return Number.isNaN(numericValue) ? value : numericValue;
  };

  const selectedVehicleValue = normalizeVehicleValue(selectedVehicle);
  const selectedDriver = selectedValues.motoristas?.[0] === 0 ? 0 : selectedValues.motoristas?.[0] || "0";
  const selectedSpeed = selectedValues.velocidade?.[0] || "0";
  const selectedDuration = selectedValues.tempominimo?.[0] || "00:00";
  const selectedEventType = selectedValues.tipoevento?.[0] || "0";

  const [initialDateTime, setInitialDateTime] = useState(
    new Date(new Date().setDate(new Date().getDate() - previousDays))
  );
  const [endDateTime, setEndDateTime] = useState(new Date());

  useEffect(() => {
    if (selectedVehicle && isManualSelection) {
      const vehicleId = selectedVehicle;
      if (vehicleId && vehicleId !== 0) {
        handleVehicle(vehicleId);
        setIsManualSelection(false);
      }
    }
  }, [selectedVehicle]);

  useEffect(() => {
    if (selectedGroup !== null && isManualSelection) {
      fetchVehiclesByGroup(selectedGroup);
      handleVehicleGroup(selectedGroup);
      setIsManualSelection(false);
    }
  }, [selectedGroup]);

  useEffect(() => {
    if (selectedDriver && isManualSelection) {
      const driverId = selectedDriver;
      if (driverId && driverId !== 0) {
        handleDriver(driverId);
        setIsManualSelection(false);
      }
    }
  }, [selectedDriver]);

  useEffect(() => {
    if (selectedSpeed && isManualSelection) {
      const speed = selectedSpeed;
      if (speed && speed !== 0) {
        handleSpeed(speed);
        setIsManualSelection(false);
      }
    }
  }, [selectedSpeed]);

  useEffect(() => {
    if (selectedDuration && isManualSelection) {
      const duration = selectedDuration;
      if (duration) {
        handleDuration(duration);
        setIsManualSelection(false);
      }
    }
  }, [selectedDuration]);

  useEffect(() => {
    if (selectedEventType && isManualSelection) {
      const eventType = selectedEventType;
      if (eventType) {
        handleEventType(eventType);
        setIsManualSelection(false);
      }
    }
  }, [selectedEventType]);

  // eslint-disable-next-line
  const fetchVehicleGroups = async () => {
    try {
      setFiltersLoading(true);
      const groups = await vehicleGroupService.fetchGroups();
      
      // Garante que sempre seja um array válido
      if (Array.isArray(groups)) {
        setVehicleGroups(groups);
        if (groups.length === 1) {
          setSelectedValues((prev) => ({
            ...prev,
            veiculos: selectedGroup ? [selectedGroup] : [],
            veiculosGroups: [selectedVehicle],
          }));
          await fetchVehiclesByGroup(groups[0].vehicleGroupId);
        } else {
          setSelectedValues((prev) => ({
            ...prev,
            veiculos: 0,
          }));
          await fetchVehiclesByGroup(0);
        }
      } else {
        // Se retornou null, undefined ou objeto de erro, define como array vazio
        setVehicleGroups([]);
        if (groups?.error) {
          console.log("Erro ao buscar grupos:", groups.message || groups.friendlyMessage);
        }
        await fetchVehiclesByGroup(0);
      }
    } catch (error) {
      console.log(error);
      setVehicleGroups([]);
    } finally {
      setFiltersLoading(false);
    }
  };

  const fetchDrivers = async () => {
    
    setFiltersLoading(true);
    try {
      const response = await vehicleService.fetchDrivers();

      // Garante que sempre seja um array válido
      if (Array.isArray(response)) {
        setDrivers(response);

        if (response.length === 1) {
          setSelectedValues((prev) => ({
            ...prev,
            motoristas: [response[0].driverId],
          }));
        } else {
          setSelectedValues((prev) => ({
            ...prev,
            motoristas: [0],
          }));
        }
      } else {
        setDrivers([]);
        if (response?.error) {
          console.log("Erro ao buscar motoristas:", response.message || response.friendlyMessage);
        }
      }
    } catch (error) {
      console.log(error);
      setDrivers([]);
    } finally {
      setFiltersLoading(false);
    }
  };

  function resolveDefaultVehicleId(vehicleList) {
    if (!Array.isArray(vehicleList)) return null;

    for (const vehicleItem of vehicleList) {
      if (vehicleItem === null || vehicleItem === undefined) continue;

      if (typeof vehicleItem === "string") {
        const upper = vehicleItem.toUpperCase?.();
        if (upper === "TODOS") continue;

        const numericValue = Number(vehicleItem);
        if (!Number.isNaN(numericValue) && numericValue !== 0) {
          return numericValue;
        }
        continue;
      }

      if (typeof vehicleItem === "number" && vehicleItem !== 0) {
        return vehicleItem;
      }

      const candidateId =
        vehicleItem.vehicleId ??
        vehicleItem.groupVehicleId ??
        vehicleItem.id ??
        null;

      if (candidateId !== null && candidateId !== undefined && candidateId !== 0) {
        return candidateId;
      }
    }

    return null;
  }

  const fetchVehiclesByGroup = async (groupId) => {
    setFiltersLoading(true);
    try {
      const vehiclesResponse = await vehicleService.fetchVehicles(groupId);

      if (Array.isArray(vehiclesResponse) && vehiclesResponse.length > 0) {
        setVehicles(vehiclesResponse);

        const defaultVehicleIdForGroup = resolveDefaultVehicleId(vehiclesResponse);
        const currentSelectedVehicle = selectedValues.veiculosGroups?.[0];

        if (props.page === "gridJourneyWork") {
          handleVehicle(defaultVehicleIdForGroup ?? "0");
        } else if (
          currentSelectedVehicle === undefined ||
          currentSelectedVehicle === null ||
          currentSelectedVehicle === "0" ||
          currentSelectedVehicle === 0 ||
          currentSelectedVehicle === "TODOS"
        ) {
          if (vehiclesFilterHasTodos) {
            handleVehicle("TODOS");
          } else {
            handleVehicle(defaultVehicleIdForGroup ?? "0");
          }
        }

        dispatch({ type: "vehicleFilter", value: groupId });
      } else {
        setVehicles([]);
        if (vehiclesResponse?.error) {
          console.log(
            "Erro ao buscar veículos:",
            vehiclesResponse.message || vehiclesResponse.friendlyMessage
          );
        }
      }
    } catch (error) {
      console.log(error);
      setVehicles([]);
    } finally {
      setFiltersLoading(false);
    }
  };

  const handleVehicle = async (vehicleId) => {
    setSelectedValues((prev) => ({
      ...prev,
      veiculosGroups: [vehicleId],
    }));
  };

  const renderVehicleGroupFilter = () => {
    return (
      <Filters
        id={"veiculos"}
        isMulti={false}
        title={t("GROUPS.SELECT_GROUP")}
        hasSubTitle={false}
        options={vehicleGroups}
        icon={HiOutlineRectangleGroup}
        hasIcon={true}
        pageTitle={props.page}
        onManualSelection={() => setIsManualSelection(true)}
        isLoading={filtersLoading}
      />
    );
  };

  const renderSpeed = () => {
    return (
      <Filters
        id={"velocidade"}
        isMulti={false}
        title={t("INFO_WINDOW.SPEED")}
        hasSubTitle={false}
        options={Array.from(Array(9).keys()).map((item) => {
          return item * 20;
        })}
        icon={FaTachometerAlt}
        hasIcon={true}
        pageTitle={props.page}
        onManualSelection={() => setIsManualSelection(true)}
      />
    );
  };

  const renderDurationTime = () => {
    return (
      <Filters
        id={"tempominimo"}
        isMulti={false}
        title={t("FILTERS_COMPONENT.MINIMUM_DURATION")}
        hasSubTitle={false}
        options={Array.from(Array(3).keys())
          .map((item) => {
            return [
              `${item}`.padStart(2, 0) + ":00",
              `${item}`.padStart(2, 0) + ":30",
            ];
          })
          .flat()}
        icon={FaRegClock}
        hasIcon={true}
        pageTitle={props.page}
        onManualSelection={() => setIsManualSelection(true)}
      />
    );
  };

  const renderEventTypeVisit = () => {
    return (
      <Filters
        id={"tipoevento"}
        isMulti={false}
        title={t("FILTERS_COMPONENT.TYPE")}
        hasSubTitle={false}
        options={[t("FILTERS_COMPONENT.BOTH"), t("FILTERS_COMPONENT.VISIT_POINT"), t("FILTERS_COMPONENT.STOP_POINT")]}
        icon={FaAlignLeft}
        hasIcon={true}
        pageTitle={props.page}
        onManualSelection={() => setIsManualSelection(true)}
      />
    );
  };

  const renderEventTypeSpeed = () => {
    return (
      <Filters
        id={"tipoevento"}
        isMulti={false}
        title={t("FILTERS_COMPONENT.TYPE")}
        hasSubTitle={false}
        options={[
          t("FILTERS_COMPONENT.ALL"),
          t("FILTERS_COMPONENT.COMPANY_SPEED_ONLY"),
          t("FILTERS_COMPONENT.ROAD_SPEED_MODERATE"),
          t("FILTERS_COMPONENT.ROAD_SPEED_SERIOUS"),
          t("FILTERS_COMPONENT.ROAD_SPEED_VERY_SERIOUS"),
          t("FILTERS_COMPONENT.ROAD_SPEED_ALL"),
        ]}
        icon={FaAlignLeft}
        hasIcon={true}
        pageTitle={props.page}
        onManualSelection={() => setIsManualSelection(true)}
      />
    );
  };

  const renderVehiclesFilter = () => {
    return (
      <Filters
        id={"veiculosGroups"}
        isMulti={false}
        title={t("FILTERS_COMPONENT.VEHICLE")}
        hasSubTitle={true}
        subTitle={t("FILTERS_COMPONENT.ALL_VEHICLES")}
        options={vehicles}
        icon={FaCar}
        hasIcon={true}
        pageTitle={props.page}
        onManualSelection={() => setIsManualSelection(true)}
        isLoading={filtersLoading}
      />
    );
  };

  const renderDriversFilter = () => {
    return (
      <Filters
        id={"motoristas"}
        isMulti={false}
        title={t("FILTERS_COMPONENT.DRIVERS")}
        hasSubTitle={true}
        subTitle={t("FILTERS_COMPONENT.ALL_DRIVERS")}
        options={drivers}
        icon={HiUser}
        hasIcon={true}
        pageTitle={props.page}
        onManualSelection={() => setIsManualSelection(true)}
      />
    );
  };

  const handleVehicleGroup = async (groupId) => {
    setSelectedValues((prev) => ({
      ...prev,
      veiculos: [groupId],
    }));
    await fetchVehiclesByGroup(groupId);
  };

  const handleSpeed = async (speedValue) => {
    setSelectedValues((prev) => ({
      ...prev,
      velocidade: [speedValue],
    }));
  };

  const handleDuration = async (duration) => {
    setSelectedValues((prev) => ({
      ...prev,
      tempominimo: [duration],
    }));
  };

  const handleEventType = async (eventType) => {
    setSelectedValues((prev) => ({
      ...prev,
      tipoevento: [eventType],
    }));
  };

  const handleDriver = async (driverId) => {
    setSelectedValues((prev) => ({
      ...prev,
      motoristas: [driverId],
    }));
  };

  const handleGenerate = async (fileType) => {
    const userSessionData = storage.get(storageKeys.settings);
    await props.handleCallback(
      selectedGroup,
      selectedVehicleValue,
      toDateTimeISOString(initialDateTime),
      toDateTimeISOString(endDateTime),
      selectedDuration,
      fileType,
      selectedSpeed,
      true,
      selectedDriver,
      selectedEventType,
      userSessionData?.userId
    );
  };

  const handleDemonstrate = async () => {
    const userSessionData = storage.get(storageKeys.settings);
    await props.handleCallback(
      selectedGroup,
      selectedVehicleValue,
      toDateTimeISOString(initialDateTime),
      toDateTimeISOString(endDateTime),
      selectedDuration,
      "",
      selectedSpeed,
      false,
      selectedDriver,
      selectedEventType,
      userSessionData?.userId
    );
  };

  const handleClear = async () => {
    props.handleClearGrid();
  };

  useEffect(() => {
    (async () => {
      handleClear();
      try {
        setFiltersLoading(true);
        await fetchVehicleGroups();
        if (props.page == "gridJourneyWork") {
          await fetchDrivers();
        }
      } catch (error) {
        console.log(error);
      } finally {
        setFiltersLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.page]);

  return (
    <div>
      <div className="row mb-3">
        {(props.page == "gridVehicleActivities" ||
          props.page == "gridDetailedVehicleActivity" ||
          props.page == "gridSpeedControl") && (
          <div className="col-md-4 col-12 mb-2 mb-md-0">{renderSpeed()}</div>
        )}
        {(props.page == "gridPeriodVisits" ||
          props.page == "gridVisitAmount") && (
          <div className="col-md-4 col-12 mb-2 mb-md-0">
            {renderDurationTime()}
          </div>
        )}
        {(props.page == "gridVisitAmount" ||
          props.page == "gridPeriodVisits") && (
          <div className="col-md-4 col-12 mb-2 mb-md-0">
            {renderEventTypeVisit()}
          </div>
        )}
        {props.page == "gridSpeedControl" && (
          <div className="col-md-4 col-12 mb-2 mb-md-0">
            {renderEventTypeSpeed()}
          </div>
        )}
      </div>

      <div className="row mb-3">
        {props.page != "gridSystemLogs" && (
          <div className="col-md-4 col-12 mb-2 mb-md-0">
            {renderVehicleGroupFilter()}
          </div>
        )}

        {props.page != "gridSystemLogs" && (
          <div className="col-md-4 col-12">{renderVehiclesFilter()}</div>
        )}

        {props.page == "gridJourneyWork" && (
          <div className="col-md-4 col-12">{renderDriversFilter()}</div>
        )}
      </div>

      <div className="row mb-4 align-items-center">
        {/* Campo de Data Início */}
        <div className="col-md-4 col-12">
          <DateTimeFilter
            id="datetimepickerEnd"
            title={t("FILTERS_COMPONENT.START")}
            value={initialDateTime}
            handleCallback={setInitialDateTime}
            placeholder={t("FILTERS_COMPONENT.START_DATE_TIME")}
            step={30}
          />
        </div>

        {/* Campo de Data Fim */}
        <div className="col-md-4 col-12">
          <DateTimeFilter
            id="datetimepickerEnd"
            title={t("FILTERS_COMPONENT.END")}
            value={endDateTime}
            handleCallback={setEndDateTime}
            placeholder={t("FILTERS_COMPONENT.END_DATE_TIME")}
            step={30}
          />
        </div>

        {/* Botão Dropdown */}
        <div className="col-md-2 col-12 text-md-end text-center">
          <Dropdown as={ButtonGroup}>
            <Dropdown.Toggle
              id="dropdown-custom-button"
              variant="primary"
              className="w-100 w-md-auto"
            >
              <FaCloudDownloadAlt className="icon-in-button" /> {t("FILTERS_COMPONENT.REPORTS")}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {props.page !== "gridDetailedVehicleActivity" && (
                <Dropdown.Item onClick={handleDemonstrate}>
                  <FaEye className="me-2" /> {t("FILTERS_COMPONENT.DISPLAY")}
                </Dropdown.Item>
              )}
              <Dropdown.Item onClick={() => handleGenerate("xls")}>
                <FaFileExcel className="me-2" /> {t("FILTERS_COMPONENT.EXCEL")}
              </Dropdown.Item>
              {props.page !== "gridDetailedVehicleActivity" && (
                <Dropdown.Item onClick={() => handleGenerate("pdf")}>
                  <FaEye className="me-2" /> {t("FILTERS_COMPONENT.PDF")}
                </Dropdown.Item>
              )}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>
    </div>
  );
}